import { useMemo } from 'react';

import { useWiredLabsFlags } from '@/core/AppShell/use-wired-labs-flags';
import { useControlMode } from '@/core/providers/ControlModeProvider';
import { useT } from '@/core/providers/I18nProvider';
import { useWorkspace } from '@/core/providers/WorkspaceProvider';
import { usePermissions } from '@/hooks/usePermissions';
import { buildSettingsSearchIndex } from '@/lib/settings-search-index';

import type { SidebarItem } from '@/components/Sidebar/Sidebar';
import type { SettingsSearchEntry } from '@/lib/settings-search-index';

/**
 * The settings-search index, for this operator, in this workspace, right now.
 *
 * Everything the index depends on is a hook read — the locale, the control mode,
 * the role, whether the workspace is personal, and the Labs flags — so this is
 * the one place those are gathered and the one `useMemo` that rebuilds when any
 * of them changes. Rebuilding is not free (every entry's haystack is normalized
 * here rather than per keystroke), which is exactly why it must not happen on
 * every render.
 *
 * Both search surfaces call this. The top-bar field ranks the result with
 * `rankSettingsSearch`; the ⌘K palette hands the same entries to its own list
 * and filters them with the same matcher, so the two can never offer different
 * answers to the same word.
 *
 * `sidebarItems` is passed in rather than read here because the shell already
 * holds them — they carry the pending-handoff count, and building a second copy
 * would mean a second set of gates to keep in step.
 */
export function useSettingsSearch(
  sidebarItems: readonly SidebarItem[],
): readonly SettingsSearchEntry[] {
  const { t, locale } = useT();
  const { can, role } = usePermissions();
  const { mode } = useControlMode();
  const { currentWorkspace } = useWorkspace();
  const labsFlags = useWiredLabsFlags();
  const isPersonal = currentWorkspace?.isPersonal === true;

  return useMemo(
    () =>
      buildSettingsSearchIndex({
        t,
        sidebarItems,
        gate: { can, role, isPersonal, isAdvanced: mode === 'advanced', labsFlags, locale },
      }),
    [t, sidebarItems, can, role, isPersonal, mode, labsFlags, locale],
  );
}
